import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { Text, View, ScrollView, Pressable } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import usePlatform from '@/utils/hooks/usePlatform';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import { LegendList } from '@legendapp/list';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import IconButton from '@/components/General/IconButton';
import TableFilters from '@/components/General/TableFilters';
import Unicon from '../Unicon';
import Checkbox from '@/components/General/inputs/Checkbox';
import DropdownMenu from '../DropdownMenu';
import SolidButton from '@/components/General/SolidButton';
import useTableSelectionStore from '@/data/store/useTableSelectionStore';
import TableCard from './TableCard';
import GuideTarget from '@/components/Guides/GuideTarget';
import { getDefaultSortDirection } from './sortUtils';

const Table = ({
    title,
    tableId: tableIdProp,
    description,
    headerRightContent,
    headerSummaryContent,
    columns,
    onRowClick,
    isLoading,
    filters,
    onFiltersChange,
    onSearchChange,
    onClearFilters,
    totalCount,
    actions,
    onRefresh,
    batchSelection = false,
    guideSelectionTarget,
    guideSortTarget,
    guideRowTarget,
    batchActions = [],
    singleBatchAction = false,
    idAccessor = 'id',
    onSelectionChange,
    sortConfig,
    onSortChange,
    enableCards = false,
    viewMode = 'table',
    onViewModeChange,
    cardHeaderRenderer,
    cardPreviewCount = 2,
    cardAccentColor,
    noBackground = false,
    estimatedItemSize = 60,
    ...props
}) => {
    const { isLandscape, isWeb } = usePlatform();
    const { theme: { colors } } = useUnistyles();
    const insets = useSafeAreaInsets();
    const [tableWidth, setTableWidth] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(48);
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);
    const [scrollX, setScrollX] = useState(0);
    const [contentWidth, setContentWidth] = useState(0);
    const horizontalScrollRef = useRef(null);
    const scrollOffsetRef = useRef(0);
    const isRestoringScrollRef = useRef(false);
    const isCardView = enableCards && viewMode === 'cards';
    const hasData = (props.data?.length ?? 0) > 0;
    const sortableColumns = useMemo(() => columns.filter(column => column.sortable), [columns]);
    const activeSortColumn = sortableColumns.find(column => column.accessor === sortConfig?.key);

    const canScrollLeft = scrollX > 1;
    const canScrollRight = contentWidth > tableWidth && scrollX < contentWidth - tableWidth - 1;

    // Selection state is stored in Zustand, keyed by tableId or title
    const tableId = tableIdProp || title;
    const storeToggleItem = useTableSelectionStore(s => s.toggleItem);
    const storeHandleSelectAll = useTableSelectionStore(s => s.handleSelectAll);
    const storeClearSelection = useTableSelectionStore(s => s.clearSelection);
    const storeIsSelected = useTableSelectionStore(s => s.isSelected);
    const storeGetSelectionCount = useTableSelectionStore(s => s.getSelectionCount);
    const tableState = useTableSelectionStore(s => s.tables[tableId]);
    const selectionMode = tableState?.selectionMode ?? 'include';
    const selectedIds = tableState?.selectedIds ?? new Set();
    const deselectedIds = tableState?.deselectedIds ?? new Set();

    // Get ID from item
    const getItemId = useCallback((item, index) => {
        let id;
        if (typeof idAccessor === 'function') {
            id = idAccessor(item);
        } else {
            id = item?.[idAccessor];
        }
        // Fall back to index if id is undefined (prevents duplicate key warnings)
        return id ?? `index-${index}`;
    }, [idAccessor]);

    // Item → index lookup map — O(1) per item instead of O(n) indexOf.
    // First-match semantics preserved: skip set() when key already exists.
    const itemIndexMap = useMemo(() => {
        if (!props.data?.length) return null;
        const map = new Map();
        props.data.forEach((item, i) => {
            if (!map.has(item)) map.set(item, i);
        });
        return map;
    }, [props.data]);

    const isItemSelected = useCallback((item) => {
        const id = getItemId(item, itemIndexMap?.get(item) ?? -1);
        return storeIsSelected(tableId, id);
    }, [tableId, tableState, getItemId, itemIndexMap, storeIsSelected]);

    // Count selected items
    const selectedCount = useMemo(() => {
        return storeGetSelectionCount(tableId, totalCount || props.data?.length || 0);
    }, [tableId, tableState, totalCount, props.data?.length, storeGetSelectionCount]);

    // Check if some (but not all) visible items are selected
    const someVisibleSelected = useMemo(() => {
        if (!props.data?.length) return false;
        const selectedVisibleCount = props.data.filter(item => isItemSelected(item)).length;
        return selectedVisibleCount > 0 && selectedVisibleCount < props.data.length;
    }, [props.data, isItemSelected]);

    // Is in "select all" mode
    const isAllSelected = selectionMode === 'exclude' && deselectedIds.size === 0;

    // Toggle individual item selection
    const toggleItemSelection = useCallback((item) => {
        const id = getItemId(item, itemIndexMap?.get(item) ?? -1);
        if (!id) return;
        storeToggleItem(tableId, id);
    }, [tableId, getItemId, itemIndexMap, storeToggleItem]);

    // Handle select all toggle
    const handleSelectAll = useCallback(() => {
        storeHandleSelectAll(tableId);
    }, [tableId, storeHandleSelectAll]);

    // Clear selection
    const clearSelection = useCallback(() => {
        storeClearSelection(tableId);
    }, [tableId, storeClearSelection]);

    // Notify parent of selection changes
    useEffect(() => {
        if (batchSelection && onSelectionChange) {
            onSelectionChange({
                mode: selectionMode,
                selectedIds: selectionMode === 'include' ? Array.from(selectedIds) : [],
                deselectedIds: selectionMode === 'exclude' ? Array.from(deselectedIds) : [],
                count: selectedCount,
                isAllSelected,
                clearSelection,
            });
        }
    }, [selectionMode, selectedIds, deselectedIds, selectedCount, isAllSelected, batchSelection, onSelectionChange, clearSelection]);

    useEffect(() => {
        if (scrollOffsetRef.current > 0 && horizontalScrollRef.current) {
            isRestoringScrollRef.current = true;
            horizontalScrollRef.current.scrollTo({ x: scrollOffsetRef.current, animated: false });
        }
    }, [props.data, sortConfig]);

    const scrollLeft = useCallback(() => {
        if (horizontalScrollRef.current) {
            const newX = Math.max(0, scrollOffsetRef.current - tableWidth);
            horizontalScrollRef.current.scrollTo({ x: newX, animated: true });
        }
    }, [tableWidth]);

    const scrollRight = useCallback(() => {
        if (horizontalScrollRef.current) {
            horizontalScrollRef.current.scrollTo({ x: scrollOffsetRef.current + tableWidth, animated: true });
        }
    }, [tableWidth]);

    const onRowClickRef = useRef(onRowClick);
    onRowClickRef.current = onRowClick;

    const isItemSelectedRef = useRef(isItemSelected);
    isItemSelectedRef.current = isItemSelected;

    const toggleItemSelectionRef = useRef(toggleItemSelection);
    toggleItemSelectionRef.current = toggleItemSelection;

    const getIsSelected = useCallback((item) => isItemSelectedRef.current(item), []);
    const handleToggleSelect = useCallback((item) => toggleItemSelectionRef.current(item), []);

    const dataLengthRef = useRef(props.data?.length || 0);
    dataLengthRef.current = props.data?.length || 0;

    const renderItem = useCallback(
        ({ item, index }) => {
            const row = (
                <TableRow
                    tableId={tableId}
                    tableWidth={tableWidth}
                    item={item}
                    itemId={getItemId(item, index)}
                    columns={columns}
                    index={index}
                    lastRow={index === dataLengthRef.current - 1}
                    actions={actions}
                    batchSelection={batchSelection}
                    getIsSelected={getIsSelected}
                    onToggleSelect={handleToggleSelect}
                    onPress={onRowClickRef.current ? () => onRowClickRef.current(item) : null}
                />
            );
            // First row doubles as the tour's "open a record" target; activate
            // runs the real row handler.
            return index === 0 && guideRowTarget ? (
                <GuideTarget id={guideRowTarget} activate={onRowClickRef.current ? () => onRowClickRef.current(item) : undefined}>{row}</GuideTarget>
            ) : row;
        },
        [tableId, tableWidth, batchSelection, columns, actions, getIsSelected, handleToggleSelect, getItemId, guideRowTarget]
    );

    const renderCardItem = useCallback(
        ({ item, index }) => (
            <TableCard
                tableId={tableId}
                item={item}
                itemId={getItemId(item, index)}
                columns={columns}
                actions={actions}
                batchSelection={batchSelection}
                onToggleSelect={handleToggleSelect}
                onPress={onRowClickRef.current ? () => onRowClickRef.current(item) : null}
                cardHeaderRenderer={cardHeaderRenderer}
                cardPreviewCount={cardPreviewCount}
                accentColor={cardAccentColor}
            />
        ),
        [tableId, columns, actions, batchSelection, handleToggleSelect, getItemId, cardHeaderRenderer, cardPreviewCount, cardAccentColor]
    );

    const keyExtractor = useCallback((item, index) => getItemId(item, index), [getItemId]);

    const viewToggleControl = enableCards ? (
        <View style={styles.viewToggle}>
            <Pressable
                testID={`${tableId}-CardView`}
                accessibilityLabel="Card view"
                onPress={() => onViewModeChange?.('cards')}
                style={styles.viewToggleButton(isCardView)}
            >
                <Unicon name="albums-outline" size={19} colorVariant={isCardView ? 'primary' : 'textSecondary'} />
            </Pressable>
            <Pressable
                testID={`${tableId}-TableView`}
                accessibilityLabel="Table view"
                onPress={() => onViewModeChange?.('table')}
                style={styles.viewToggleButton(!isCardView)}
            >
                <Unicon name="list-outline" size={20} colorVariant={!isCardView ? 'primary' : 'textSecondary'} />
            </Pressable>
        </View>
    ) : null;

    const cardToolbar = (batchSelection || enableCards || (sortableColumns.length > 0 && onSortChange)) && (
        <View style={styles.cardSortBar}>
            {batchSelection && (
                <View style={styles.cardSelectAll}>
                    <Checkbox
                        testID={`${tableId}-CardSelectAll`}
                        checked={isAllSelected}
                        indeterminate={!isAllSelected && (someVisibleSelected || selectedCount > 0)}
                        onChange={handleSelectAll}
                        size={18}
                    />
                    <Text style={styles.cardSelectAllText}>Select all</Text>
                </View>
            )}
            {sortableColumns.length > 0 && onSortChange && (
                <View style={styles.cardSortActions}>
                    <DropdownMenu
                        testID={`${tableId}-CardSort`}
                        options={sortableColumns.map(column => ({
                            name: column.value,
                            selected: column.accessor === sortConfig?.key,
                            onSelect: () => onSortChange({ key: column.accessor, direction: column.accessor === sortConfig?.key ? sortConfig.direction : getDefaultSortDirection(column) }),
                        }))}
                        triggerComponent={
                            <View style={styles.cardSortSelect}>
                                <Unicon name="swap-vertical-outline" size={18} colorVariant="textSecondary" />
                                <Text style={styles.cardSortText}>{activeSortColumn ? `Sort: ${activeSortColumn.value}` : 'Sort'}</Text>
                                <Unicon name="chevron-down" size={16} colorVariant="textSecondary" />
                            </View>
                        }
                    />
                    {sortConfig && (
                        <>
                            <Pressable
                                testID={`${tableId}-CardSortDirection`}
                                accessibilityLabel={sortConfig.direction === 'asc' ? 'Sort descending' : 'Sort ascending'}
                                onPress={() => onSortChange({ ...sortConfig, direction: sortConfig.direction === 'asc' ? 'desc' : 'asc' })}
                                style={styles.cardSortButton}
                            >
                                <Unicon name={sortConfig.direction === 'asc' ? 'arrow-up' : 'arrow-down'} size={18} colorVariant="primary" />
                            </Pressable>
                            <Pressable testID={`${tableId}-CardSortClear`} accessibilityLabel="Clear sort" onPress={() => onSortChange(null)} style={styles.cardSortButton}>
                                <Unicon name="close" size={18} colorVariant="textSecondary" />
                            </Pressable>
                        </>
                    )}
                    {viewToggleControl}
                </View>
            )}
            {!(sortableColumns.length > 0 && onSortChange) && viewToggleControl}
        </View>
    );

    const emptyContent = (
        <View style={styles.emptyState(tableWidth)}>
            {isLoading ? (
                <View testID={`${tableId}-Loading`} style={styles.loadingState}>
                    <ActivityIndicator size="small" color={colors.primary} />
                </View>
            ) : (
                props.ListEmptyComponent || <EmptyListPlaceholder spacing={20} size={100} message="No data available" />
            )}
        </View>
    );

    const loadingOverlay = isLoading && hasData ? (
        <View pointerEvents="none" testID={`${tableId}-Loading`} style={styles.loadingOverlay(isCardView, tableWidth, scrollX)}>
            <View style={styles.loadingIndicator}>
                <ActivityIndicator size="small" color={colors.primary} />
            </View>
        </View>
    ) : null;

    return <View style={[styles.outerWrapper, noBackground && styles.noBackground]}>
        {batchSelection && selectedCount > 0 && (
            <View style={styles.selectionBar}>
                <View style={styles.selectionBarLeft}>
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={!isAllSelected && selectedCount > 0}
                        onChange={handleSelectAll}
                        size={18}
                    />
                    <Text style={styles.selectionText}>
                        {isAllSelected ? 'All' : selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
                    </Text>
                </View>
                <View style={styles.selectionBarRight}>
                    <Pressable onPress={clearSelection} style={styles.clearButton}>
                        <Text style={styles.clearButtonText}>Clear</Text>
                    </Pressable>
                    {batchActions.length > 0 && batchSelection && (singleBatchAction && batchActions.length === 1 ? (
                        <SolidButton
                            testID="CampaignSendSelected"
                            size="sm"
                            type="primary"
                            text={batchActions[0].name}
                            onPress={() => {
                                batchActions[0].onSelect(selectionMode, selectionMode === 'include' ? selectedIds : deselectedIds, selectedCount);
                                clearSelection();
                            }}
                        />
                    ) : <DropdownMenu testID="BatchActions" options={batchActions.map(a => ({
                        ...a,
                        onSelect: () => {
                            a.onSelect(selectionMode, selectionMode == 'include' ? selectedIds : deselectedIds, selectedCount)
                            clearSelection()
                        }
                    }))} />)}
                </View>
            </View>
        )}

        {(title || description || headerRightContent || headerSummaryContent || filters || onSearchChange) &&
            <View style={styles.headerWrapper}>
                {/* Top row: Title + Actions */}
                <View style={styles.headerTopRow}>
                    <View style={styles.titleSection}>
                        {title && <Text style={styles.titleText}>{title}</Text>}
                        {description && <Text style={styles.titleSubtext}>{description}</Text>}
                    </View>
                    <View style={styles.actionsSection}>
                        {headerRightContent}
                        {onRefresh && (
                            <IconButton
                                iconName="refresh"
                                iconSize={22}
                                type="textSecondary"
                                disabled={isLoading}
                                tooltipText="Refresh"
                                testID={`${tableId}-Refresh`}
                                onPress={onRefresh}
                            />
                        )}
                    </View>
                </View>

                {/* Filters row */}
                {(filters || onSearchChange) && (
                    <View style={styles.filtersRow}>
                        <TableFilters 
                            tableName={title} 
                            filters={filters} 
                            onFiltersChange={onFiltersChange} 
                            onSearchChange={onSearchChange} 
                            onClearFilters={onClearFilters}
                        />
                    </View>
                )}

                {headerSummaryContent && (
                    <View style={styles.headerSummaryRow}>
                        {headerSummaryContent}
                    </View>
                )}

                {/* Results count */}
                {totalCount > 0 && (
                    <View style={styles.resultsCountRow}>
                        <Text style={styles.resultsCountText}>{totalCount} results</Text>
                    </View>
                )}
            </View>
        }

        

        <View 
            style={styles.tableArea}
            {...(isWeb ? {
                onMouseEnter: () => setIsHeaderHovered(true),
                onMouseLeave: () => setIsHeaderHovered(false),
            } : {})}
        >
            <View onLayout={(e) => setTableWidth(e.nativeEvent.layout.width)} style={styles.container}>
                {tableWidth > 0 && (isCardView ? (
                    <View style={styles.flex1}>
                        {cardToolbar}
                        <View style={styles.listArea}>
                            {hasData ? (
                                <LegendList
                                    {...props}
                                    estimatedItemSize={estimatedItemSize + 70}
                                    renderItem={renderCardItem}
                                    keyExtractor={keyExtractor}
                                    data={props.data ?? []}
                                    extraData={[columns, sortConfig]}
                                    keyboardShouldPersistTaps="handled"
                                    contentContainerStyle={{
                                        ...(props.contentContainerStyle || {}),
                                        paddingTop: 3,
                                        ...(insets.bottom > 0 ? { paddingBottom: ((props.contentContainerStyle?.paddingBottom) || 0) + insets.bottom } : {}),
                                    }}
                                />
                            ) : emptyContent}
                            {loadingOverlay}
                        </View>
                    </View>
                ) : (
                    <View style={styles.flex1}>
                        {viewToggleControl && <View style={styles.tableModeToolbar}>{viewToggleControl}</View>}
                        <ScrollView
                            horizontal
                            ref={horizontalScrollRef}
                            onScroll={(e) => {
                                const x = e.nativeEvent.contentOffset.x;
                                scrollOffsetRef.current = x;
                                setScrollX(x);
                            }}
                            onContentSizeChange={(w, _h) => setContentWidth(w)}
                            scrollEventThrottle={16}
                            keyboardShouldPersistTaps="handled"
                        >
                            <View style={styles.flex1}>
                                <View onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}>
                                    <TableHeader
                                        tableId={tableId}
                                        guideSelectionTarget={guideSelectionTarget}
                                        guideSortTarget={guideSortTarget}
                                        columns={columns}
                                        tableWidth={tableWidth-1}
                                        hasActions={actions?.length > 0}
                                        batchSelection={batchSelection && hasData}
                                        isAllSelected={isAllSelected}
                                        isIndeterminate={!isAllSelected && (someVisibleSelected || selectedCount > 0)}
                                        onSelectAll={handleSelectAll}
                                        sortConfig={sortConfig}
                                        onSortChange={onSortChange}
                                    />
                                </View>
                                <View style={styles.listArea}>
                                    {hasData ? (
                                        <LegendList
                                            {...props}
                                            estimatedItemSize={estimatedItemSize}
                                            renderItem={renderItem}
                                            keyExtractor={keyExtractor}
                                            data={props.data ?? []}
                                            extraData={columns}
                                            keyboardShouldPersistTaps="handled"
                                            contentContainerStyle={{
                                                ...(props.contentContainerStyle || {}),
                                                ...(insets.bottom > 0 ? { paddingBottom: ((props.contentContainerStyle?.paddingBottom) || 0) + insets.bottom } : {}),
                                            }}
                                        />
                                    ) : emptyContent}
                                    {loadingOverlay}
                                </View>
                            </View>
                        </ScrollView>
                    </View>
                ))}
            </View>

            {!isCardView && isLandscape && canScrollLeft && (
                <Pressable 
                    style={[
                        styles.scrollArrow, 
                        styles.scrollArrowLeft, 
                        { 
                            height: headerHeight,
                            opacity: isWeb ? (isHeaderHovered ? 0.9 : 0.1) : 0.35,
                            backgroundColor: isWeb && isHeaderHovered ? colors.surfaceVariant : colors.surfaceVariant + '80',
                        }
                    ]}
                    onPress={scrollLeft}
                >
                    <Unicon name="chevron-back" size={20} colorVariant="textSecondary" />
                </Pressable>
            )}
            {!isCardView && isLandscape && canScrollRight && (
                <Pressable 
                    style={[
                        styles.scrollArrow, 
                        styles.scrollArrowRight, 
                        { 
                            height: headerHeight,
                            opacity: isWeb ? (isHeaderHovered ? 0.9 : 0.1) : 0.35,
                            backgroundColor: isWeb && isHeaderHovered ? colors.surfaceVariant : colors.surfaceVariant + '80',
                        }
                    ]}
                    onPress={scrollRight}
                >
                    <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                </Pressable>
            )}
        </View>
    </View>
}

const styles = StyleSheet.create(({colors, fonts, shadows}, rt) => ({
    outerWrapper: {
        borderRadius: rt.breakpoint === 'xs' ? 0 : 3,
        marginHorizontal: rt.breakpoint === 'xs' ? 0 : 10,
        backgroundColor: rt.themeName === 'dark' ? colors.surface : colors.surfaceElevated,
        marginBottom: rt.breakpoint === 'xs' ? 0 : 10,
        flex: 1,
        overflow: "hidden",
        boxShadow: 'none',
    },
    noBackground: {
        marginHorizontal: 0,
        marginBottom: 0,
        borderRadius: 0,
        boxShadow: 'none',
        backgroundColor: 'transparent',
    },
    selectionBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 15,
        paddingVertical: 10,
        backgroundColor: colors.primaryContainer,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    selectionBarLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    selectionBarRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    selectionText: {
        fontFamily: fonts.bold,
        color: colors.text,
        fontSize: 14,
    },
    clearButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 3,
        backgroundColor: colors.surface,
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    clearButtonText: {
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontSize: 13,
    },
    headerWrapper: {
        padding: rt.breakpoint === 'xs' ? 10 : 16,
        paddingBottom: rt.breakpoint === 'xs' ? 6 : 8,
        gap: rt.breakpoint === 'xs' ? 6 : 12,
    },
    headerTopRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'nowrap',
    },
    titleSection: {
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 120,
    },
    titleText: {
        fontFamily: fonts.bold,
        color: colors.text,
        fontSize: rt.breakpoint === 'xs' ? 14 : 16,
        letterSpacing: -0.2,
        textTransform: 'none',
        marginBottom: rt.breakpoint === 'xs' ? 0 : 2,
    },
    titleSubtext: {
        fontFamily: fonts.body, 
        color: colors.textSecondary, 
        fontSize: 12,
        opacity: rt.breakpoint === 'xs' ? 0 : 1,
        maxHeight: rt.breakpoint === 'xs' ? 0 : 100,
        overflow: 'hidden',
    },
    actionsSection: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        flexWrap: 'nowrap',
        justifyContent: 'flex-end',
    },
    viewToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 2,
        borderRadius: 3,
        backgroundColor: colors.surfaceVariant,
    },
    viewToggleButton: (active) => ({
        width: 30,
        height: 28,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 3,
        backgroundColor: active ? colors.surfaceElevated : 'transparent',
        boxShadow: 'none',
    }),
    tableModeToolbar: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingHorizontal: 10,
        paddingBottom: 4,
    },
    cardSortBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 6,
        paddingHorizontal: 10,
        paddingBottom: 4,
    },
    cardSelectAll: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
    },
    cardSelectAllText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
    cardSortActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    cardSortSelect: {
        minHeight: 34,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        borderRadius: 3,
        backgroundColor: colors.surfaceVariant,
    },
    cardSortText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
    cardSortButton: {
        width: 34,
        height: 34,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 3,
        backgroundColor: colors.surfaceVariant,
    },
    filtersRow: {
        marginTop: 4,
    },
    headerSummaryRow: {
        marginTop: rt.breakpoint === 'xs' ? -1 : -6,
    },
    resultsCountRow: {
        paddingTop: rt.breakpoint === 'xs' ? 2 : 4,
    },
    resultsCountText: {
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontSize: 12,
    },
    container: {
        flex: 1,
        overflowX: "auto",
    },
    tableArea: {
        flex: 1,
        position: 'relative',
    },
    scrollArrow: {
        position: 'absolute',
        top: 0,
        width: 36,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        _web: {
            cursor: 'pointer',
            transition: 'opacity 0.25s ease, background-color 0.25s ease',
        },
    },
    scrollArrowLeft: {
        left: 0,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    scrollArrowRight: {
        right: 0,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    flex1: {
        flex: 1,
    },
    listArea: {
        flex: 1,
        minHeight: 0,
        position: 'relative',
    },
    emptyState: (width) => ({
        flex: 1,
        width: width || '100%',
        minHeight: 180,
        alignItems: 'center',
        justifyContent: 'center',
    }),
    loadingState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 40,
    },
    loadingOverlay: (cardView, width, scrollX) => ({
        position: 'absolute',
        top: 0,
        ...(cardView ? { left: 0, right: 0 } : { left: scrollX, width }),
        alignItems: 'center',
        paddingTop: 10,
        zIndex: 20,
    }),
    loadingIndicator: {
        width: 44,
        height: 32,
        borderRadius: 3,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.surfaceVariant,
        boxShadow: shadows.card,
    }
}));

export default React.memo(Table);
